import type { ExtractedStatement } from './extractedStatements';

export type Segment =
  | { kind: 'plain'; text: string }
  | { kind: 'highlight'; text: string; statementIndex: number };

export type ComputeSegmentsResult = {
  segments: Segment[];
  /** Indices that were dropped from inline marks (overlapping spans).
   * They still appear as cards in the expander, but have no inline
   * anchor. */
  droppedStatementIndices: number[];
  /** True when at least one span's `end` exceeded `body.length` and we
   * fell back to plain rendering for the entire body. */
  fallbackUsed: boolean;
};

/**
 * Splits `messageBody` into a sequence of plain and highlighted
 * segments based on `extractedStatements` source spans. Mirrors the
 * questions equivalent algorithm so a future generalization change
 * can collapse the two implementations behind a generic `{
 * sourceStartIndex, sourceEndIndex }` shape.
 *
 * Rules:
 * - Statements with null `sourceStartIndex` or `sourceEndIndex` are
 *   skipped (still rendered as cards by the expander, but no inline
 *   anchor).
 * - Spans are sorted by `sourceStartIndex`; later overlapping spans
 *   are dropped from inline marks.
 * - If any span's `end` exceeds `body.length`, fall back to a single
 *   plain segment for the whole body.
 */
export function computeSegments(
  body: string,
  extractedStatements: ExtractedStatement[]
): ComputeSegmentsResult {
  if (extractedStatements.length === 0) {
    return {
      segments: [{ kind: 'plain', text: body }],
      droppedStatementIndices: [],
      fallbackUsed: false,
    };
  }

  const candidates = extractedStatements.flatMap((statement, statementIndex) => {
    const { sourceStartIndex, sourceEndIndex } = statement;

    if (sourceStartIndex === null || sourceEndIndex === null) {
      return [];
    }

    return [{ statementIndex, start: sourceStartIndex, end: sourceEndIndex }];
  });

  if (candidates.length === 0) {
    return {
      segments: [{ kind: 'plain', text: body }],
      droppedStatementIndices: [],
      fallbackUsed: false,
    };
  }

  for (const { start, end } of candidates) {
    if (start < 0 || end > body.length || end <= start) {
      return {
        segments: [{ kind: 'plain', text: body }],
        droppedStatementIndices: [],
        fallbackUsed: true,
      };
    }
  }

  const sorted = candidates.slice().sort((a, b) => a.start - b.start);

  const accepted: { statementIndex: number; start: number; end: number }[] = [];

  const dropped: number[] = [];

  let lastAcceptedEnd = -1;

  for (const { statementIndex, start, end } of sorted) {
    if (start < lastAcceptedEnd) {
      dropped.push(statementIndex);

      continue;
    }

    accepted.push({ statementIndex, start, end });

    lastAcceptedEnd = end;
  }

  const segments: Segment[] = [];

  let cursor = 0;

  for (const span of accepted) {
    if (span.start > cursor) {
      segments.push({ kind: 'plain', text: body.slice(cursor, span.start) });
    }

    segments.push({
      kind: 'highlight',
      text: body.slice(span.start, span.end),
      statementIndex: span.statementIndex,
    });

    cursor = span.end;

    cursor = skipOrdinalMarker(body, cursor, span.statementIndex);
  }

  if (cursor < body.length) {
    segments.push({ kind: 'plain', text: body.slice(cursor) });
  }

  return {
    segments,
    droppedStatementIndices: dropped,
    fallbackUsed: false,
  };
}

/**
 * Skips a leading ` S{n+1} ` ordinal marker in the body immediately
 * after a highlight span. Defensive against test data or upstream
 * tooling that inlines the chip ordinal as literal text — the chip
 * component renders `S{n+1}` itself, so leaving the literal in the
 * plain segment would produce visible duplication.
 */
function skipOrdinalMarker(body: string, fromIndex: number, statementIndex: number): number {
  const ordinal = `S${statementIndex + 1}`;

  const slice = body.slice(fromIndex);

  const match = slice.match(/^[ \t]{0,2}(S\d+)[ \t]{0,2}/);

  if (!match || match[1] !== ordinal) {
    return fromIndex;
  }

  return fromIndex + match[0].length;
}

/**
 * Where each segment begins in the body. Segments are slices of the body in
 * order, but not always contiguous, because a stray ordinal marker after a
 * span is skipped, so each start is found from the end of the one before.
 */
export function segmentStarts(body: string, segments: readonly Segment[]): number[] {
  let cursor = 0;

  return segments.map((segment) => {
    const start = body.indexOf(segment.text, cursor);

    cursor = start + segment.text.length;

    return start;
  });
}
